/**
 * sl-m82f.4.3: Shared drill-down state + filter logic for feature tabs.
 *
 * Every tab shows the same two dropdowns (By User, By Model) and has to
 * filter its breakdown rows the same way. This hook owns the state + the
 * filter/option derivation so tab components only describe their data
 * shape, not the filter plumbing.
 *
 * Unknown-user / unknown-model rows are hidden while a filter is active
 * for that dimension (they can never match a specific value anyway).
 */
import { useCallback, useMemo, useState } from 'react';

import type { BreakdownRow } from './types';

export interface DrillDownState {
  userFilter: string | null;
  modelFilter: string | null;
  setUserFilter: (value: string | null) => void;
  setModelFilter: (value: string | null) => void;
  reset: () => void;
  filtered: BreakdownRow[];
  userOptions: Array<{ value: string; label: string }>;
  modelOptions: Array<{ value: string; label: string }>;
}

function collectOptions(
  rows: BreakdownRow[],
  accessor: (row: BreakdownRow) => string | null | undefined,
  labeler: (row: BreakdownRow) => string
): Array<{ value: string; label: string }> {
  const map = new Map<string, string>();
  for (const row of rows) {
    const value = accessor(row);
    if (!value) continue;
    if (!map.has(value)) map.set(value, labeler(row));
  }
  return [...map.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Derives filtered rows + option lists for the By User / By Model selects.
 *
 * `userLabeler` / `modelLabeler` produce the dropdown label for a given
 * row — typically the row's `name` (for model rows) or `subtext` (for user
 * rows). Passing them in keeps the hook decoupled from where the label
 * lives on the row.
 */
export function useDrillDown(
  rows: BreakdownRow[],
  {
    userLabeler,
    modelLabeler,
  }: {
    userLabeler?: (row: BreakdownRow) => string;
    modelLabeler?: (row: BreakdownRow) => string;
  } = {}
): DrillDownState {
  const [userFilter, setUserFilter] = useState<string | null>(null);
  const [modelFilter, setModelFilter] = useState<string | null>(null);

  const reset = useCallback(() => {
    setUserFilter(null);
    setModelFilter(null);
  }, []);

  const userOptions = useMemo(
    () =>
      collectOptions(
        rows,
        row => row.userId ?? null,
        userLabeler ?? (row => row.name)
      ),
    [rows, userLabeler]
  );

  const modelOptions = useMemo(
    () =>
      collectOptions(
        rows,
        row => row.model ?? null,
        modelLabeler ?? (row => row.model ?? row.name)
      ),
    [rows, modelLabeler]
  );

  const filtered = useMemo(() => {
    if (!userFilter && !modelFilter) return rows;
    return rows.filter(row => {
      if (userFilter && row.userId !== userFilter) return false;
      if (modelFilter && row.model !== modelFilter) return false;
      return true;
    });
  }, [rows, userFilter, modelFilter]);

  return {
    userFilter,
    modelFilter,
    setUserFilter,
    setModelFilter,
    reset,
    filtered,
    userOptions,
    modelOptions,
  };
}
